import React, { FC, useEffect } from 'react'
import { Form, Forms, useFormsContext } from '@ally/forms'
import {
  Box,
  Label,
  TextBody,
  useSteppedProcess,
  ButtonGroup,
  Button,
  useModalContext,
} from '@ally/metronome-ui'
import allytm from '@ally/allytm'
import { Beneficiary } from '../../../types'
import { FundsAllocationFields } from '../../common/FundsAllocationFields'
import { KeyValuePairObj, fireErrorTagging } from '../../../utils/tagging'

export type AllocationStepIraValues = Record<string, string | number>

export const allocationStepIraInitialValues = (
  existingBenes: Beneficiary[] | undefined,
  newBenePercentage: string,
): AllocationStepIraValues =>
  existingBenes && existingBenes.length
    ? existingBenes.reduce(
        (acc, curr) => ({
          ...acc,
          [curr.beneficiaryId]: curr.allocationPercentage
            ? parseInt(curr.allocationPercentage, 10).toString()
            : // istanbul ignore next
              '0',
        }),
        {
          newBeneficiaryFundAllocation: '0',
        },
      )
    : // istanbul ignore next // low value
      {
        newBeneficiaryFundAllocation: newBenePercentage,
      }

export const AllocationForm: FC<{
  filteredIraBenes?: Beneficiary[]
  setAllocationStepIraValues: React.Dispatch<
    React.SetStateAction<AllocationStepIraValues>
  >
}> = ({ filteredIraBenes, setAllocationStepIraValues }) => {
  const { setOpen } = useModalContext()
  const { formId, values, handleSubmit, setFieldValue, errors, setFormError } =
    useFormsContext()

  const filteredIds =
    filteredIraBenes && filteredIraBenes.map(bene => bene.beneficiaryId)

  const filteredValues = Object.keys(values).filter(
    id =>
      (filteredIds && filteredIds.includes(id)) ||
      id === 'newBeneficiaryFundAllocation',
  )
  const total = filteredValues.reduce((acc, curr) => {
    if (Number.isNaN(Number(values[curr]))) {
      return acc
    }
    return acc + Number(values[curr])
  }, 0)

  const handleInputChange = (name: string, value: string): void => {
    setFieldValue(name, value)
    setAllocationStepIraValues(
      // istanbul ignore next // low value
      prevState => ({
        ...prevState,
        [name]: value,
      }),
    )
  }

  const onSubmit = (): void => {
    // istanbul ignore next - tagging
    if (total !== 100) {
      fireErrorTagging({
        beneficiaryAllocation: 'Your total allocation must add up to 100%',
      } as KeyValuePairObj)
    }
    // istanbul ignore next - tagging
    fireErrorTagging(errors as KeyValuePairObj)
    if (total === 100 || !filteredIraBenes || filteredIraBenes.length === 0) {
      setFormError('')
      handleSubmit()
    }
  }

  return (
    <Form id={formId}>
      {filteredIraBenes && filteredIraBenes.length > 0 ? (
        <FundsAllocationFields
          existingBeneficiaries={filteredIraBenes}
          total={total}
          setPercentageValue={handleInputChange}
        />
      ) : (
        <Box mt="md" mb="md">
          <Label htmlFor="allocationAmount">Allocation amount</Label>
          <TextBody tag="span" size="sm">
            100%
          </TextBody>
        </Box>
      )}
      <Box my="md">
        <ButtonGroup>
          <Button
            allytmln="continue from allocation"
            text="Continue"
            onClick={(): void => onSubmit()}
            data-testid="submit-continue-button"
            // disabled={}
          />
          <Button
            allytmln="Cancel Add Beneficiary"
            text="Cancel"
            onClick={
              // istanbul ignore next
              (): void => setOpen(false)
            }
            variant="link"
          />
        </ButtonGroup>
      </Box>
    </Form>
  )
}

export const AllocationStepIra: FC<{
  filteredIraBenes?: Beneficiary[]
  allocationStepIraValues: AllocationStepIraValues
  setAllocationStepIraValues: React.Dispatch<
    React.SetStateAction<AllocationStepIraValues>
  >
}> = ({
  filteredIraBenes,
  allocationStepIraValues,
  setAllocationStepIraValues,
}) => {
  const { setNextStepActive } = useSteppedProcess()

  // istanbul ignore next - Low value - Just going to next steps
  const handleSubmit = (): void => {
    setNextStepActive()
  }

  useEffect(() => {
    allytm.event(
      'modalView',
      'AOS:Beneficiaries:Bank IRAs:Add Beneficiary:Allocation',
      {
        treatAsPageview: true,
      },
    )
  }, [])

  return (
    <Forms
      formId="beneficiary-classification-form"
      initialValues={allocationStepIraValues}
      onSubmit={handleSubmit}
    >
      <AllocationForm
        filteredIraBenes={filteredIraBenes}
        setAllocationStepIraValues={setAllocationStepIraValues}
      />
    </Forms>
  )
}
